import {
    CustomerContactPersonModel,
    ICustomerContactPersonModel,
    TourEmailModel,
    VehicleModel,
} from "@core/services/api.service";
import { FormControl, UntypedFormBuilder } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Actions, Select, Store, ofActionSuccessful } from "@ngxs/store";
import {
    Component,
    Input,
    Inject,
    Output,
    EventEmitter,
    ViewChild,
    ChangeDetectorRef,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { DataSourceConfig } from "@core/models";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Observable, Subject, Subscription } from "rxjs";
import { AddVehicleDialogComponent } from "../add-vehicle-dialog/add-vehicle-dialog.component";
import {
    GetTourByIdAction,
    GetTourByIdSuccessAction,
} from "@ngxs-store/managed-tour-store/managed-tour.actions";
import { CustomerState } from "@ngxs-store/customer-store/customer.state";
import {
    DeleteCustomerContactPersonAction,
    DeleteEmailAction,
    EditCustomerContactPersonAction,
    EditCustomerContactPersonSuccessAction,
    GetCustomerContactPersonAction,
} from "@ngxs-store/customer-store/customer.actions";
import { takeUntil } from "rxjs/operators";
import { CustomerStoreModule } from "@ngxs-store/customer-store/customer-store.module";

@Component({
    selector: "app-edit-contact-person-dialog",
    templateUrl: "./edit-contact-person-dialog.component.html",
})
export class EditContactPersonDialogComponent implements OnDestroy, OnInit {
    @Select(CustomerState.getCustomerContacts) custContacts$: Observable<CustomerContactPersonModel[]>;

    @Input() custContact: CustomerContactPersonModel[];
    @Input() isLoading: boolean;
    @Input() isMobileMode: boolean;
    @Input() id: number;
    @Output() updateDataSourceConfig = new EventEmitter<DataSourceConfig>();
    @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort!: MatSort;

    email: string;
    private onDestroy = new Subject<void>();
    searchControl = new FormControl();
    selectedRows: Set<any> = new Set();
    preSelectedEmails: String;
    sub: Subscription;
    transportOrderNumber: string | null = null;

    allEmails: CustomerContactPersonModel[] = [];
    selectedEmails: MatTableDataSource<CustomerContactPersonModel> = new MatTableDataSource<CustomerContactPersonModel>([]);

    dataSource = new MatTableDataSource<CustomerContactPersonModel>(
        this.allEmails
    );

    dataSourceConfig: DataSourceConfig = {
        name: "emails-table",
        columns: ["select", "email"],
        desktopModeColumns: [],
        mobileModeColumns: ["email", "actions"],
        pageIndex: 10,
        pageSize: 10,
        pageSizeOptions: [10, 50, 100],
        filter: "",
        filterColumns: ["email"],
        selectedFilterColumns: ["email"],
        sortBy: "",
        sortByOrder: "",
    };

    constructor(
        private formBuilder: UntypedFormBuilder,
        public dialogRef: MatDialogRef<EditContactPersonDialogComponent>,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) data: any,
        private store: Store,
        private dialog: MatDialog
    ) {
        this.id = data.editTourId;
        this.preSelectedEmails = data.emails;
        this.transportOrderNumber = data.transportOrderNumber ?? null;
    }

    ngOnInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.dataSource.filterPredicate = (data: CustomerContactPersonModel, filter: string) => {
            const searchStr = filter.toLowerCase().trim();
            return data.email ? data.email.toLowerCase().includes(searchStr) : false;
        };

        this.searchControl.valueChanges
            .pipe(takeUntil(this.onDestroy))
            .subscribe(searchValue => {
                const filterValue = searchValue?.trim().toLowerCase() || '';
                this.dataSource.filter = filterValue;

                if (this.dataSource.paginator) {
                    this.dataSource.paginator.firstPage();
                }
            });

        this.sub = this.custContacts$.subscribe((emails) => {
            if (emails) {
                this.allEmails = emails;
                this.dataSource.data = this.allEmails;

                this.initializePreSelectedEmails();
            }
        });

        this.store.dispatch(new GetCustomerContactPersonAction());
    }

    enableEdit(element: any): void {
        this.dataSource.data.forEach((row) => (row.isEditing = false)); // Disable editing for other rows
        element.isEditing = true;
    }

    saveEmail(element: any): void {
        element.isEditing = false;
        console.log('Updated email:', element.email);
        // Add logic to persist the updated email to the backend if needed
    }


    private initializePreSelectedEmails(): void {
        if (this.preSelectedEmails) {
            this.selectedEmails.data = [];

            const emailArray = this.preSelectedEmails.split(';').map((email) => email.trim());

            const preselected = this.allEmails.filter((email) =>
                emailArray.includes(email.email)
            );

            this.selectedEmails.data = [...preselected];
            this.selectedEmails._updateChangeSubscription();
        }
    }

    deleteEmail(row: CustomerContactPersonModel) {

        this.store.dispatch(new DeleteEmailAction(row.id));

        const index = this.selectedEmails.data.findIndex((item) => item.email === row.email);

        if (index > -1) {
            this.selectedEmails.data.splice(index, 1);
        }

        // Lets go ahead and delete the email address
        const index2 = this.allEmails.findIndex((item) => item.email === row.email);

        if (index2 > -1) {
            this.allEmails.splice(index2, 1);

            this.dataSource.data = this.allEmails;
        }

        this.selectedEmails._updateChangeSubscription();
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    toggleSelection(row: CustomerContactPersonModel): void {
        const index = this.selectedEmails.data.findIndex((item) => item.email === row.email);

        if (index > -1) {
            this.selectedEmails.data.splice(index, 1);
        } else {
            this.selectedEmails.data.push(row);
        }

        this.selectedEmails._updateChangeSubscription();
    }

    isSelected(row: CustomerContactPersonModel): boolean {
        return this.selectedEmails.data.some((item) => item.email === row.email);
    }

    toggleSelectAll(event: Event): void {
        const checkbox = event.target as HTMLInputElement;
        if (checkbox.checked) {
            this.selectedEmails.data = [...this.allEmails];
        } else {
            this.selectedEmails.data = [];
        }
        this.selectedEmails._updateChangeSubscription();
    }

    isAllSelected(): boolean {
        return (
            this.selectedEmails.data.length === this.allEmails.length &&
            this.selectedEmails.data.length > 0
        );
    }

    isSomeSelected(): boolean {
        return (
            this.selectedEmails.data.length > 0 &&
            this.selectedEmails.data.length < this.allEmails.length
        );
    }

    assign() {
        //Get emails out of selectedRows
        const emailList = Array.from(this.selectedEmails.data).map(
            (row) => new TourEmailModel({ id: row.id, email: row.email })
        );

        if (emailList) {
            this.store.dispatch(
                new EditCustomerContactPersonAction(this.id, emailList)
            );

            const sub1 = this.actions$
                .pipe(
                    ofActionSuccessful(EditCustomerContactPersonSuccessAction)
                )
                .subscribe(
                    (successAction: EditCustomerContactPersonSuccessAction) => {
                        if (successAction) {
                            this.store.dispatch(new GetTourByIdAction(this.id, this.transportOrderNumber));
                            this.dialogRef.close();
                        }

                        sub1.unsubscribe();
                    }
                );
        }
    }

    onAddClick() {
        const dialog = this.dialog.open(AddVehicleDialogComponent, {
            data: { id: this.id, isEmail: true, redirectBack: true, transportOrderNumber: this.transportOrderNumber }, disableClose: true, width: '500px'
        });

        dialog.afterClosed().subscribe((result) => {
            if (result) {
                this.store.dispatch(new GetTourByIdAction(this.id, this.transportOrderNumber));

                const sub1 = this.actions$
                    .pipe(ofActionSuccessful(GetTourByIdSuccessAction))
                    .subscribe((successAction: GetTourByIdSuccessAction) => {
                        if (successAction) {
                            this.dialogRef.close();
                            this.cdr.detectChanges();
                        }

                        sub1.unsubscribe();
                    });
            }
        });
    }

    applyDataSourceConfig() {
        if (this.dataSource.paginator) {
            this.dataSource.filter = this.dataSourceConfig.filter;
            this.dataSource.paginator.pageIndex =
                this.dataSourceConfig.pageIndex;
            this.dataSource.paginator.pageSize = this.dataSourceConfig.pageSize;
            this.dataSource.paginator.pageSizeOptions =
                this.dataSourceConfig.pageSizeOptions;
        }

        if (this.dataSource.sort) {
            this.dataSource.sort.direction = this.dataSourceConfig.sortByOrder;
            this.dataSource.sort.active = this.dataSourceConfig.sortBy;
        }
    }

    page(pageEvent: PageEvent) {
        this.updateDataSourceConfig.emit({
            ...this.dataSourceConfig,
            pageIndex: pageEvent.pageIndex,
            pageSize: pageEvent.pageSize,
        });
    }

    sortChange(sortEvent: Sort) {
        this.updateDataSourceConfig.emit({
            ...this.dataSourceConfig,
            sortByOrder: sortEvent.direction,
            sortBy: sortEvent.active,
        });
    }

    filterPredicate(data: VehicleModel, filter: string): boolean {
        if (this.dataSourceConfig.selectedFilterColumns.length > 0) {
            return this.dataSourceConfig.selectedFilterColumns.length === 1
                ? data[this.dataSourceConfig.selectedFilterColumns[0]]
                    .toString()
                    .toLowerCase()
                    .includes(filter.trim().toLowerCase())
                : this.dataSourceConfig.selectedFilterColumns
                    .reduce((prevVal, currVal, idx) => {
                        return idx === 1
                            ? data[prevVal] + data[currVal]
                            : prevVal + data[currVal];
                    })
                    .toString()
                    .toLowerCase()
                    .includes(filter.trim().toLowerCase());
        }
    }

    ngOnDestroy() {
        this.onDestroy.next();
        this.onDestroy.complete();
        this.sub.unsubscribe();
    }
}
